import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import useGetChainInfo from '@/hooks/useGetChainInfo';
import { maskAddressValue } from '@/utils/format';

import { EventTriggerType, HypurrEulerNetPositionsStrategy, Trigger } from '../../domain';
import { TriggerType } from './components/TriggerType';
import { TriggerValueDetail } from './components/TriggerValueDetail';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const HypurrEulerNetPositionsTriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const depositTokenAddress = useMemo(() => {
    return trigger.context?.depositTokenAddress || '';
  }, [trigger.context]);

  const debtTokenAddress = useMemo(() => {
    return trigger.context?.debtTokenAddress || '';
  }, [trigger.context]);

  const positionType = useMemo(() => {
    const strategy = trigger.context?.strategy;
    return strategy === HypurrEulerNetPositionsStrategy.Deposit
      ? 'Deposit'
      : strategy === HypurrEulerNetPositionsStrategy.Borrow
        ? 'Borrow'
        : '';
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    const chainId = trigger.context?.chainId;
    if (!chainId) return null;
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [trigger.context]);

  const chainIconUrl = useMemo(() => {
    if (!networkInfo) return '';
    return getChainIconUrl(networkInfo.chainId) || '';
  }, [networkInfo, getChainIconUrl]);

  const pairDisplay = useMemo(() => {
    if (!depositTokenAddress || !debtTokenAddress) return '';

    return (
      <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
        {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
        <span>
          {maskAddressValue(depositTokenAddress)} - {maskAddressValue(debtTokenAddress)}
        </span>
      </div>
    );
  }, [depositTokenAddress, debtTokenAddress, chainIconUrl, networkInfo]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <TriggerType eventType={triggerType} />

      <TriggerValueDetail label="Pair" value={pairDisplay} />

      <TriggerValueDetail label="Position Type" value={positionType} />
    </div>
  );
};
